import React from "react";
import SeoHead from "../seo/SeoHead";
import ServicePageShell from "../ui/ServicePageShell";
import { ServiceTag } from "../ui/ServiceTag";
import CallButton from "../ui/CallButton";
import DirectionsButton from "../ui/DirectionsButton";
import BusinessHours from "../ui/BusinessHours";
import ReviewSection from "../ui/ReviewSection";
import TrustBadges from "../ui/TrustBadges";
import VehicleBrandGrid from "../ui/VehicleBrandGrid";
import WarrantyNotice from "../ui/WarrantyNotice";
import ServiceAreas from "../ui/ServiceAreas";
import PageBreadcrumb from "../seo/PageBreadcrumb";
import { Link } from "react-router-dom";
import { PHONE_DISPLAY } from "../../lib/businessInfo";
import heroImg from "../../assets/European.png";

const bulletClass =
  "flex items-start gap-2 text-sm text-slate-700 sm:text-base leading-relaxed";

const EuropeanAutoRepair = () => {
  return (
    <>
      <SeoHead
        title="European Auto Repair in Temecula | BMW, Mercedes & More"
        description="European car repair in Temecula: factory maintenance, diagnostics, and quality parts for BMW, Mercedes-Benz, Audi, and other imports. Visit Advanced Automotive Smog."
        path="/european-auto-repair"
      />

      <ServicePageShell>
        <PageBreadcrumb
          label="European Auto Repair"
          path="/european-auto-repair"
          description="European car repair in Temecula: factory maintenance, diagnostics, and quality parts for BMW, Mercedes-Benz, Audi, and other imports."
          serviceType="European vehicle repair"
        />
        <div className="flex min-w-0 flex-col gap-8 md:flex-row md:items-start md:gap-14">
          <div className="order-1 flex w-full min-w-0 justify-center md:order-none md:w-2/5">
            <div className="relative w-full max-w-xs sm:max-w-sm">
              <div className="absolute -inset-3 rounded-3xl bg-teal-600/10 blur-xl" />
              <img
                src={heroImg}
                alt="European vehicle service and repair"
                className="relative aspect-[4/3] h-auto w-full max-h-[14rem] rounded-3xl border border-slate-200 object-cover object-center shadow-xl sm:aspect-auto sm:max-h-none"
              />
            </div>
          </div>

          <div className="w-full min-w-0 md:w-3/5">
            <ServiceTag>European · Import service</ServiceTag>
            <h1 className="mb-3 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              European Auto Repair
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-slate-700 sm:text-base">
              European vehicles often need specific fluids, procedures, and
              attention to detail. We service imports with the care they demand—
              from scheduled maintenance to diagnostics and mechanical repairs.
            </p>
            <CallButton className="px-8 py-3 text-base" />
          </div>
        </div>

        <TrustBadges tone="light" className="mt-12" />
        <VehicleBrandGrid tone="light" className="mt-10" linked />
        <WarrantyNotice tone="light" className="mt-8" />

        <section className="mt-14 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Signs You May Need This Service
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Warning lights for engine, ABS, or stability systems</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Strange noises from suspension, drivetrain, or engine</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Performance issues: turbo lag, rough idle, or cooling concerns</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Service reminders or overdue factory intervals</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Services Included
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Factory-scheduled maintenance and inspections</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Engine, cooling, brake, and suspension repairs</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>
                Electrical and{" "}
                <Link to="/check-engine-light" className="text-teal-600 hover:underline">
                  computer diagnostics
                </Link>
              </span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Fluid services matched to manufacturer specifications</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>
                <Link
                  to="/services/smog-emissions-services"
                  className="text-teal-600 hover:underline"
                >
                  Smog and emissions testing
                </Link>{" "}
                for applicable models
              </span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Why Choose Us
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            Our shop is known for high-end and import vehicles alongside everyday
            drivers. We respect the engineering in European cars and focus on
            correct procedures, quality parts, and honest timelines.
          </p>
        </section>

        <BusinessHours tone="light" className="mt-12" />
        <ReviewSection tone="light" className="mt-12" />

        <ServiceAreas tone="light" className="mt-12 border-t border-slate-200 pt-12" />

        <section className="mt-12 rounded-2xl border border-slate-200 bg-teal-50/80 p-6 text-center shadow-sm ring-1 ring-teal-600/10 sm:p-10">
          <h2 className="text-lg font-semibold text-navy-900 sm:text-xl">
            Service your European vehicle with us
          </h2>
          <p className="mt-2 text-sm text-slate-700 sm:text-base">
            Call {PHONE_DISPLAY} or tap directions to visit our shop on Rio Nedo.
          </p>
          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <CallButton className="px-8 py-3 text-base" />
            <DirectionsButton className="px-8 py-3 text-base" />
          </div>
        </section>
      </ServicePageShell>
    </>
  );
};

export default EuropeanAutoRepair;
